// Global Instructions Rule Applied!
import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import { validateAccountCommitmentGridModel } from '../../../state_manager/data_models/account_commitments_grid_model.ts'
import { readJsonBody } from '../../../core/utils/utilities.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

export interface CommitmentSort {
  key: string
  direction: 'asc' | 'desc'
}
export interface CommitmentFilter {
  key: string
  value: unknown
}

export interface FetchAccountCommitmentsResponse {
  // Data rows + appended per-currency Total rows (Total rows carry _total_label).
  data: Record<string, unknown>[]
  hasMore: boolean
  totalCount: number
  error?: string
}

export async function fetchAccountCommitments(
  accountId: string,
  opts: { sorting?: CommitmentSort[]; filters?: CommitmentFilter[]; page?: number; pageSize?: number } = {},
  accessToken: string | null = null
): Promise<FetchAccountCommitmentsResponse> {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const requestBody = {
      data: {
        page: opts.page ?? 1,
        page_size: opts.pageSize ?? 500,
        sorting: (opts.sorting ?? []).map((s) => ({ key: s.key, direction: s.direction })),
        filters: (opts.filters ?? []).map((f) => ({ key: f.key, value: f.value }))
      }
    }

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.COMPANIES.COMMITMENTS_FILTER, { id: accountId }),
      { method: API_ROUTES.COMPANIES.COMMITMENTS_FILTER.method, headers, body: JSON.stringify(requestBody) }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      const body = await readJsonBody<{ message?: string }>(response)
      throw new Error(body?.message || `Failed to fetch commitments: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: unknown[]; has_more?: boolean; count?: number }
    const rawRows = Array.isArray(json.data) ? json.data : []

    // Validate data rows (skip the synthesised Total rows); a row that fails is
    // dropped with a dev warning rather than failing the whole tab.
    const out: Record<string, unknown>[] = []
    for (const row of rawRows) {
      const r = row as Record<string, unknown>
      if (r && r._total_label) {
        out.push(r) // Total row — passed through untouched
        continue
      }
      try {
        out.push(validateAccountCommitmentGridModel(r))
      } catch (e) {
        if (process.env.NODE_ENV === 'development') console.warn('Commitment row failed validation:', e, r)
      }
    }

    return {
      data: out,
      hasMore: Boolean(json.has_more),
      totalCount: typeof json.count === 'number' ? json.count : out.length
    }
  } catch (error) {
    return {
      data: [],
      hasMore: false,
      totalCount: 0,
      error: error instanceof Error ? error.message : 'An unexpected error occurred while fetching commitments'
    }
  }
}
